import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import StarIcon from "@mui/icons-material/Star";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Typography,
  Skeleton,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import {
  getWithQueryApiServices,
  postWithQueryApiServices,
  putApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { ROUTE_PATHS } from "../../../routes/routePath";
import { successToast, errorToast } from "../../../utils/helper";
import PaymentForm from "../../shared/PaymentForm";
import Navbar from "../../shared/Navbar"; // ✅ Import your updated Navbar

const PRIMARY_COLOR = "#7285b3";
const PRIMARY_DARK = "#5a6a94";
const PRIMARY_LIGHT = "#8fa0c7";

const SubscriptionChoices = () => {
  const navigate = useNavigate();
  const cookies = new Cookies();

  const [open, setOpen] = useState(false);
  const [type, setType] = useState("paid");
  const [payment, setPayment] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [processingPlanId, setProcessingPlanId] = useState(null); // Track which plan is being processed

  const defaultcompany = cookies.get("defaultcompany");
  const userID = cookies.get("userId");
  const userFullName = cookies.get("userFullName");
  const activeSubscription = cookies.get("activeSubscription");

  const { 
    data: subscriptionData, 
    isLoading,
    isError,
    error 
  } = useQuery({
    queryKey: ["subscriptionLists"],
    queryFn: () => getWithQueryApiServices(apiRoutes.GET_SUBSCRIPTION_LIST),
  });

  const startDate = moment().add(7, "days").format("MMMM D, YYYY");

  const processPayment = ({ invoiceID, formData }) => {
    // Prevent multiple submissions
    if (isProcessing) return;
    
    setPayment(formData);
    setSelectedSubscription(invoiceID);
    setProcessingPlanId(invoiceID.subscriptionID); // Track which plan is processing
    createInvoice.mutate(invoiceID);
  };

  const createInvoice = useMutation({
    mutationKey: ["createInvoiceform"],
    mutationFn: (subscription) =>
      postWithQueryApiServices(
        apiRoutes.GET_INVOICE_LIST,
        `selectedCompany=${defaultcompany}`,
        {
          ...subscription,
          duedate: type === "free" ? moment().add(7, "days") : moment().toDate(),
          invoicedate: moment().toDate(),
          companyID: defaultcompany,
          invoiceitems: JSON.parse(subscription?.subscriptionitem),
        }
      ),
    onSuccess: ({ data }) => {
      createPayment.mutate({
        invoiceID: data.meta.last_row_id,
        formData: payment,
      });
    },
    onError: (error) => {
      setProcessingPlanId(null); // Reset processing state on error
      errorToast(error?.response?.data?.message || "Failed to create invoice");
    },
  });

  const createPayment = useMutation({
    mutationKey: ["payInvoiceform"],
    mutationFn: ({ invoiceID, formData }) =>
      postWithQueryApiServices(
        `${apiRoutes.PAY_INVOICE}/${invoiceID}`,
        `selectedCompany=${defaultcompany}`,
        { ...formData, type: type }
      ),
    onSuccess: () => {
      updateUser.mutate();
    },
    onError: (error) => {
      setProcessingPlanId(null); // Reset processing state on error
      errorToast(error?.response?.data?.message || "Payment failed");
    },
  });

  const updateUser = useMutation({
    mutationKey: [`updateUserform`],
    mutationFn: () =>
      putApiServices(`${apiRoutes.UPDATE_USER}/${userID}/subscribe`, {
        subscriptionID: selectedSubscription.subscriptionID,
      }),
    onSuccess: () => {
      cookies.set("activeSubscription", true, { path: "/" });
      setOpen(false);
      setProcessingPlanId(null); // Reset processing state
      successToast("Subscribed Successfully!");
      navigate(ROUTE_PATHS.HOME);
    },
    onError: (error) => {
      setProcessingPlanId(null); // Reset processing state on error
      errorToast(error?.response?.data?.message || "Failed to update subscription");
    },
  });

  const isProcessing = createInvoice.isPending || createPayment.isPending || updateUser.isPending;
  // Handle modal close - prevent closing while processing
  const handleModalClose = () => {
    if (!isProcessing) {
      setOpen(false);
      setProcessingPlanId(null);
    }
  };

  // Handle plan selection
  const handleSelectPlan = (subscriptionId, planType) => {
    if (isProcessing) return; // Prevent opening another modal while processing
    setType(planType);
    setOpen(subscriptionId);
  };

  const renderSkeletons = () => (
    <Grid container spacing={4} justifyContent="center">
      {[1, 2, 3].map((item) => (
        <Grid item xs={12} sm={6} md={4} key={item}>
          <Card sx={{ height: 450, borderRadius: 3 }}>
            <CardContent>
              <Skeleton variant="text" width="60%" height={40} />
              <Skeleton variant="text" width="40%" height={50} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mt: 2 }} />
              <Skeleton variant="rectangular" height={180} sx={{ mt: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={50} sx={{ mt: 2, borderRadius: 2 }} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // Get current processing step for display
  const getProcessingStep = () => {
    if (createInvoice.isPending) return "Creating invoice...";
    if (createPayment.isPending) return "Processing payment...";
    if (updateUser.isPending) return "Activating subscription...";
    return "Processing...";
  };

  return (
    <Box 
      sx={{ 
        display: "flex", 
        flexDirection: "column", 
        minHeight: "100vh",
        backgroundColor: PRIMARY_COLOR,
      }}
    >
      {/* ✅ Minimal Navbar - Only Logo and Profile */}
      <Navbar minimal={true} />

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        
        {/* Welcome Banner (for new users) */}
        {!activeSubscription && (
          <Box
            sx={{
              background: PRIMARY_COLOR,
              color: "white",
              py: { xs: 3, sm: 5 },
              px: { xs: 2, sm: 3 },
              textAlign: "center",
            }}
          >
            <Container maxWidth="md">
              <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}>
                Welcome{userFullName ? `, ${userFullName}` : ""}! 👋
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 1, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                Your tenant has been created successfully.
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                Choose a subscription plan below to unlock all features and get started.
              </Typography>
            </Container>
          </Box>
        )}

        {/* Page Title (for returning users) */}
        {activeSubscription && (
          <Box 
            sx={{ 
              py: { xs: 3, sm: 4 }, 
              px: { xs: 2, sm: 3 }, 
              textAlign: "center",
              backgroundColor: "white",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Container maxWidth="md">
              <Typography 
                variant="h4" 
                component="h1" 
                fontWeight="bold" 
                gutterBottom
                sx={{ color: PRIMARY_COLOR, fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
              >
                Subscription Plans
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                Upgrade or change your current subscription
              </Typography>
            </Container>
          </Box>
        )}

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, flexGrow: 1 }}>
          
          {isError && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error?.message || "Failed to load subscription plans. Please try again."}
            </Alert>
          )}

          {isLoading && renderSkeletons()}

          {!isLoading && !isError && (
            <Grid container spacing={{ xs: 3, sm: 4 }} justifyContent="center">
              {subscriptionData?.data?.list?.map((subscription, index) => {
                const isPopular = index === 1;
                const isThisPlanProcessing = processingPlanId === subscription.subscriptionID;
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={subscription.subscriptionID}>
                    <Card
                      elevation={isPopular ? 8 : 2}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.3s ease",
                        borderRadius: 3,
                        position: "relative",
                        border: isPopular ? `2px solid ${PRIMARY_COLOR}` : "1px solid #e0e0e0",
                        opacity: isProcessing && !isThisPlanProcessing ? 0.6 : 1, // Dim other cards while processing
                        pointerEvents: isProcessing && !isThisPlanProcessing ? "none" : "auto",
                        "&:hover": {
                          transform: isProcessing ? "none" : "translateY(-8px)",
                          boxShadow: isProcessing ? undefined : `0 12px 40px rgba(114, 133, 179, 0.25)`,
                        },
                      }}
                    >
                      {isPopular && (
                        <Chip
                          icon={<StarIcon sx={{ color: "white !important" }} />}
                          label="Most Popular"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: -12,
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontWeight: "bold",
                            backgroundColor: PRIMARY_COLOR,
                            color: "white",
                            "& .MuiChip-icon": { color: "white" },
                          }}
                        />
                      )}

                      <CardContent sx={{ flexGrow: 1, pt: { xs: isPopular ? 3 : 2, sm: isPopular ? 4 : 3 }, px: { xs: 2, sm: 3 } }}>
                        <Box sx={{ textAlign: "center", mb: 3 }}>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            sx={{ color: PRIMARY_COLOR }}
                            gutterBottom
                          >
                            {subscription.title}
                          </Typography>
                          
                          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", my: 2 }}>
                            <Typography variant="h3" fontWeight="bold" color="text.primary">
                              ${subscription.amount}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              /month
                            </Typography>
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 3 }}>
                          {subscription.description}
                        </Typography>

                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: "#4b4b4c" }}>
                          What's included:
                        </Typography>
                        <List dense disablePadding>
                          {subscription.benfits.split(",").map((benefit, idx) => (
                            <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 20, color: PRIMARY_COLOR }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary={benefit.trim()} 
                                primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                              />
                            </ListItem>
                          ))}
                        </List>

                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: "#4b4b4c" }}>
                            Includes:
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {JSON.parse(subscription.subscriptionitem).map((item) => (
                              <Chip
                                key={item.subscriptionitemID}
                                icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
                                label={`${item.item}${item.quantity > 1 ? ` × ${item.quantity}` : ""}`}
                                variant="outlined"
                                size="small"
                                sx={{ 
                                  fontSize: 11,
                                  borderColor: item.recurring ? PRIMARY_COLOR : "#ccc",
                                  color: item.recurring ? PRIMARY_COLOR : "text.secondary",
                                  "& .MuiChip-icon": { color: item.recurring ? PRIMARY_COLOR : "#999" },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </CardContent>

                      <Box sx={{ p: 3, pt: 0 }}>
                        <Button
                          variant={isPopular ? "contained" : "outlined"}
                          fullWidth
                          size="large"
                          disabled={isProcessing}
                          onClick={() => handleSelectPlan(subscription.subscriptionID, "paid")}
                          sx={{
                            py: 1.5,
                            fontWeight: "bold",
                            borderRadius: 2,
                            backgroundColor: isPopular ? PRIMARY_COLOR : "transparent",
                            borderColor: PRIMARY_COLOR,
                            color: isPopular ? "white" : PRIMARY_COLOR,
                            "&:hover": {
                              backgroundColor: isPopular ? PRIMARY_DARK : PRIMARY_LIGHT,
                              borderColor: PRIMARY_DARK,
                              color: "white",
                            },
                            "&:disabled": {
                              backgroundColor: isPopular ? "#ccc" : "transparent",
                              borderColor: "#ccc",
                              color: "#999",
                            },
                          }}
                        >
                          {isThisPlanProcessing ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <CircularProgress size={20} color="inherit" />
                              Processing...
                            </Box>
                          ) : isPopular ? (
                            "Get Started"
                          ) : (
                            "Choose Plan"
                          )}
                        </Button>
                        
                        <Button
                          variant="text"
                          fullWidth
                          size="small"
                          disabled={isProcessing}
                          onClick={() => handleSelectPlan(subscription.subscriptionID, "free")}
                          sx={{ 
                            py: 0.5, 
                            mt: 1, 
                            color: isProcessing ? "#999" : PRIMARY_COLOR,
                          }}
                        >
                          Start 7-day free trial
                        </Button>
                      </Box>

                      {/* Payment Modal */}
                      <Modal
                        open={open === subscription.subscriptionID}
                        onClose={handleModalClose}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: { xs: "90%", sm: 450 },
                            bgcolor: "background.paper",
                            boxShadow: 24,
                            p: { xs: 2, sm: 4 },
                            borderRadius: 3,
                            maxHeight: "90vh",
                            overflow: "auto",
                          }}
                        >
                          <Box sx={{ textAlign: "center", mb: 3 }}>
                            <Typography variant="h5" fontWeight="bold" sx={{ color: PRIMARY_COLOR }}>
                              {type === "free" ? "Start Free Trial" : "Complete Payment"}
                            </Typography>
                            
                            <Chip
                              label={subscription.title}
                              sx={{ mt: 1, backgroundColor: PRIMARY_COLOR, color: "white" }}
                            />
                            
                            <Typography variant="h4" fontWeight="bold" sx={{ mt: 2 }}>
                              ${subscription.amount}
                              <Typography component="span" variant="body1" color="text.secondary">
                                /month
                              </Typography>
                            </Typography>
                          </Box>

                          {type === "free" && (
                            <Alert severity="info" sx={{ mb: 3 }}>
                              Your 7-day free trial starts today. Billing begins on <strong>{startDate}</strong>.
                            </Alert>
                          )}

                          {/* Processing Overlay */}
                          {isProcessing && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 10,
                                borderRadius: 3,
                              }}
                            >
                              <CircularProgress size={48} sx={{ color: PRIMARY_COLOR, mb: 2 }} />
                              <Typography variant="h6" fontWeight="bold" sx={{ color: PRIMARY_COLOR }}>
                                {getProcessingStep()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Please don't close this window
                              </Typography>
                            </Box>
                          )}

                          <PaymentForm
                            invoiceID={subscription}
                            allowedMethods={type === "free" ? ["ach"] : ["card", "ach"]}
                            onSubmit={(arg) => processPayment(arg)}
                            isLoading={isProcessing}
                            disabled={isProcessing}
                          />

                          <Button
                            fullWidth
                            variant="text"
                            onClick={handleModalClose}
                            disabled={isProcessing}
                            sx={{ 
                              mt: 2, 
                              color: isProcessing ? "#ccc" : "text.secondary",
                            }}
                          >
                            {isProcessing ? "Please wait..." : "Cancel"}
                          </Button>
                        </Box>
                      </Modal>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {!isLoading && !isError && !subscriptionData?.data?.list?.length && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No subscription plans available
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default SubscriptionChoices;